import React from "react";
import {
  Box,
  Button,
  Grid,
  Link,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DOMPurify from "dompurify";
import { useAppLoadingContext } from "../../../../Context/AppLoadingContext";
import { AppLoadingContextTypes } from "../../../../Context/ContextTypes/AppLoadingContextTypes";
import "react-lazy-load-image-component/src/effects/blur.css";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useTheme } from "@mui/material/styles";

type FourthVariantPropTypes = {
  earnCardId: string;
  earnCardBorderRadiusTop: string;
  earnCardBorderRadiusBottom: string;
  earnCardVariant: string;
  earnCardHeaderText: string;
  earnCardButtonText: string;
  earnCardButtonUrl: string;
  earnCardButtonAriaLabel: string;
  earnCardButtonId: string;
  earnCardSubHeader: string;
  earnCardSubParagraph: string;
  earnCardSubLink: string;
  earnCardSubLinkUrl: string;
  earnCardSubLinkId: string;
  earnCardButtonImageItems: Array<{
    itemImageSrc: string;
    itemImageHeight: string;
    itemImageWidth: string;
    itemImageAltText: string;
    itemLinkUrl: string;
    itemLinkAriaLabel: string;
  }>;
  earnCardImage: string;
  earnCardImageWidth: string;
  earnCardImageHeight: string;
  earnCardImageLeftRightMargin: string;
  earnCardImageAltText: string;

  isInEditor: boolean;
  cqPath: string;
  cqType: string;
};
export default function FourthVariant(props: FourthVariantPropTypes) {
  // translation
  const { translationsData } = useAppLoadingContext() as AppLoadingContextTypes;
  const DomPurify = (tag: string) => {
    return DOMPurify.sanitize(translationsData[tag]);
  };

  const translatedData = {
    // title and button
    earnCardHeaderText: DomPurify(props.earnCardHeaderText),
    earnCardButtonText: DomPurify(props.earnCardButtonText),
    earnCardButtonUrl: DomPurify(props.earnCardButtonUrl),
    earnCardButtonAriaLabel: DomPurify(props.earnCardButtonAriaLabel),
    earnCardSubHeader: DomPurify(props.earnCardSubHeader),
    earnCardSubParagraph: DomPurify(props.earnCardSubParagraph),
    earnCardSubLink: DomPurify(props.earnCardSubLink),
    earnCardSubLinkUrl: DomPurify(props.earnCardSubLinkUrl),
    earnCardImage: DomPurify(props.earnCardImage),
    earnCardImageWidth: props.earnCardImageWidth,
    earnCardImageHeight: props.earnCardImageHeight,
    earnCardImageAltText: DomPurify(props.earnCardImageAltText),
  };

  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));

  return (
    <Box
      component="section"
      id={props.earnCardId}
      sx={{
        margin: {
          xs: "30px 0px",
          sm: "30px 0px",
          md: "50px 0px",
          lg: "50px 0px 50px 10px",
        },
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Grid
        container
        display="flex"
        flexDirection="row"
        justifyContent="center"
        maxWidth={{
          xs: "100%",
          sm: "auto",
          md: "100%",
          lg: "1280px",
        }}
        marginLeft={{ xs: "16px", sm: "16px", lg: "auto" }}
        marginRight={{ xs: "16px", sm: "16px", lg: "auto" }}
        sx={{
          paddingTop: {xs:"30px", sm:'60px'},
          backgroundColor: "#F8F8F8",
          borderRadius: props.earnCardBorderRadiusTop,
        }}
      >
        <Grid
          item
          xs={12}
          display="flex"
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            margin={{ xs:"60px 0px 60px 0px",sm: '0px 0px 40px 0px',lg:'60px 0px'}}
            // padding={{
            //   xs: "0px 14px 0px 14px",
            //   sm: "0px 20px 0px 20px",
            //   md: "0px 40px 0px 40px",
            //   lg: "0px 0px 0px 104px",
            // }}
          >
            <LazyLoadImage
              effect="blur"
              delayTime={1000}
              src={translatedData.earnCardImage}
              width={isMobile ? "auto" : translatedData.earnCardImageWidth}
              height={isMobile ? "auto" : translatedData.earnCardImageHeight}
              alt=""
              style={{
                display: "flex",
                padding: `0px ${props.earnCardImageLeftRightMargin}`,
              }}
            />

            {/* title */}
            <Typography
              variant="h2"
              display="block"
              fontWeight="700"
              textAlign="center"
              margin={{
                xs: "16px 0px",
                sm: "16px 0px",
                md: "16px 0px",
                lg: "16px 0px",
              }}
              lineHeight={{ xs: "36px", sm: "36px", lg: "44px" }}
              fontSize={{ xs: "28px", sm: "28px", lg: "36px" }}
            >
              {translatedData.earnCardHeaderText}
            </Typography>

            {translatedData.earnCardSubParagraph ? (
              <Typography
                variant="body1"
                display="block"
                fontSize="16px"
                textAlign={{
                  xs: "center",
                  sm: "center",
                  md: "center",
                  lg: "left",
                }}
                sx={{
                  lineHeight: "24px",
                  margin: {
                    xs: "0px 11px",
                    sm: "0px 100px",
                    md: "0px 20px",
                    lg: "0px",
                  },
                }}
              >
                {translatedData.earnCardSubParagraph}
              </Typography>
            ) : null}

            <Button
              component={Link}
              variant="contained"
              id={props.earnCardButtonId || `FourthVariantApplyNowBtn`}
              sx={{
                padding: {
                  xs: "20px 25px !important",
                  sm: "20px 25px !important",
                  lg: "20px 60px !important",
                },
                marginTop: { xs:'20px',sm:'20px', lg:'40px'},
                marginBottom: { xs:'0px', sm:'0px',lg:'30px'},
                minWidth: { xs: "auto", sm: "50%", md: "50%", lg: "auto" },
                whiteSpace: "nowrap",
                width: {
                  xs: "85% !important",
                  sm: "auto !important",
                  md: "auto !important",
                  lg: "auto !important",
                },
                fontSize: "16px !important",
                lineHeight: "18px !important",
              }}
              href={translatedData.earnCardButtonUrl}
              className="primary-btn"
            >
              {translatedData.earnCardButtonText}
            </Button>
          </Grid>
        </Grid>

        {/* triangle color borders */}
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Box
            component="div"
            className="trianleBgBorder"
            sx={{
              borderBottomLeftRadius: props.earnCardBorderRadiusBottom
                ? {
                    xs: "10px",
                    sm: props.earnCardBorderRadiusBottom,
                    lg: props.earnCardBorderRadiusBottom,
                  }
                : { xs: "10px", sm: "10px", lg: "30px" },

              borderBottomRightRadius: props.earnCardBorderRadiusBottom
                ? {
                    xs: "10px",
                    sm: props.earnCardBorderRadiusBottom,
                    lg: props.earnCardBorderRadiusBottom,
                  }
                : { xs: "10px", sm: "10px", lg: "30px" },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
